<template>
  <KekkleCard>
    <div
      v-if="mode === 'resetPassword'"
      class="flex flex-col items-center justify-center"
    >
      <h1 class="text-xl font-bold dark:text-slate-200">
        {{ $t("auth.reset_password") }}
      </h1>
      <text-input
        v-model="password"
        type="password"
        class="mb-4 w-full"
        autocomplete="password"
        :placeholder="$t('global.inputs.placeholders.password')"
        :label="$t('global.inputs.labels.password')"
        :error-message="$t('global.inputs.errors.password_not_strong')"
        :has-error="!passwordHasMinimumLength"
      ></text-input>
      <text-input
        v-model="passwordRepeat"
        type="password"
        class="mb-4 w-full"
        autocomplete="password"
        :placeholder="$t('global.inputs.placeholders.repeat_password')"
        :label="$t('global.inputs.labels.repeat_password')"
        :error-message="$t('global.inputs.errors.password_not_same')"
        :has-error="!passwordRepeatValid"
      ></text-input>
      <p
        v-if="passwordHasMinimumLength"
        class="w-full text-left text-green-500"
      >
        ✅ {{ $t("auth.minimum_8_chars") }}
      </p>
      <p v-if="!passwordHasMinimumLength" class="w-full text-left text-red-500">
        ❌{{ $t("auth.no_minimum_8_chars") }}
      </p>
      <p v-if="passwordHasNumber" class="w-full text-left text-green-500">
        ✅{{ $t("auth.contains_number") }}
      </p>
      <p v-if="!passwordHasNumber" class="w-full text-left text-red-500">
        ❌ {{ $t("auth.contains_no_number") }}
      </p>
      <p v-if="passwordHasSpecialChar" class="w-full text-left text-green-500">
        ✅ {{ $t("auth.contains_special_char") }}
      </p>
      <p v-if="!passwordHasSpecialChar" class="w-full text-left text-red-500">
        ❌{{ $t("auth.does_not_contain_special_char") }}
      </p>
      <p v-if="passwordRepeatValid" class="w-full text-left text-green-500">
        ✅ {{ $t("auth.password_same") }}
      </p>
      <p v-if="!passwordRepeatValid" class="w-full text-left text-red-500">
        ❌ {{ $t("auth.password_not_same") }}
      </p>

      <default-button
        class="mx-0"
        :disabled="!resetValid"
        :loading="resetting"
        @click="resetPassword()"
      >
        <p>{{ $t("auth.reset_password") }}</p>
      </default-button>
    </div>
    <div v-if="mode === 'verifyEmail'">
      <h1
        v-if="!successfullVerified"
        class="text-xl font-bold dark:text-slate-200"
      >
        {{ $t("auth.verifying_email") }}
      </h1>
      <h1
        v-if="successfullVerified"
        class="text-xl font-bold dark:text-slate-200"
      >
        {{ $t("auth.account_verified") }}
      </h1>
    </div>
    <div
      v-if="mode === 'verifyAndChangeEmail'"
      class="flex flex-col items-center justify-center"
    >
      <h1 class="mb-4 text-xl font-bold dark:text-slate-200">
        {{ emailChangeVerified ? "✅" : "⏳" }}
        {{
          $t(
            emailChangeVerified
              ? "auth.email_change_verified"
              : "auth.verifying_email_change",
          )
        }}
      </h1>
      <p class="mb-4 text-center dark:text-slate-200">
        {{
          $t(
            emailChangeVerified
              ? "auth.email_change_success"
              : "auth.verifying_email_change_message",
          )
        }}
      </p>
      <default-button v-if="emailChangeVerified" @click="continueToApp">
        {{ $t("auth.continue") }}
      </default-button>
      <div v-else class="flex items-center justify-center">
        <spinner class="h-8 w-8 animate-spin" />
      </div>
    </div>
    <div
      v-if="mode === 'recoverEmail'"
      class="flex flex-col items-center justify-center"
    >
      <h1 class="mb-4 text-xl font-bold dark:text-slate-200">
        {{ emailRecovered ? "✅" : "⏳" }}
        {{
          $t(emailRecovered ? "auth.email_recovered" : "auth.recovering_email")
        }}
      </h1>
      <p class="mb-4 text-center dark:text-slate-200">
        {{
          $t(
            emailRecovered
              ? "auth.email_recover_success"
              : "auth.recovering_email_message",
          )
        }}
      </p>
      <default-button v-if="emailRecovered" @click="continueToApp">
        {{ $t("auth.continue") }}
      </default-button>
      <div v-else class="flex items-center justify-center">
        <spinner class="h-8 w-8 animate-spin" />
      </div>
    </div>
  </KekkleCard>
</template>
<script>
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
  checkActionCode,
} from "firebase/auth";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { setErrorToast, setSuccessToast } from "@/helpers/notifications/toast";
import TextInput from "../components/Inputs/TextInput.vue";
import DefaultButton from "../components/Buttons/DefaultButton.vue";
import Spinner from "../components/Spinner.vue";

import { firebaseAuth } from "@/helpers/firebase/firebase";
import * as Sentry from "@sentry/vue";
import KekkleCard from "@/components/ui/KekkleCard.vue";

export default {
  name: "EmailAction",
  components: { TextInput, DefaultButton, Spinner, KekkleCard },
  setup() {
    const authStore = useAuthStore();
    const userStore = useUserStore();
    const groupsStore = useGroupsStore();
    return { authStore, userStore, groupsStore };
  },
  data() {
    return {
      resetting: false,
      resendEmailSend: false,
      mode: "",
      actionCode: "",
      password: "",
      passwordRepeat: "",
      successfullVerified: false,
      emailChangeVerified: false,
      emailRecovered: false,
    };
  },
  computed: {
    passwordValid() {
      return this.email.length > 1;
    },
    passwordHasMinimumLength() {
      return this.password.length > 7;
    },
    passwordRepeatValid() {
      return this.password === this.passwordRepeat && this.password.length > 0;
    },
    passwordHasNumber() {
      return /\d/.test(this.password);
    },
    passwordHasSpecialChar() {
      const specialChars = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
      return specialChars.test(this.password);
    },
    resetValid() {
      return (
        this.passwordRepeatValid &&
        this.passwordHasMinimumLength &&
        this.passwordHasNumber &&
        this.passwordHasSpecialChar
      );
    },
  },
  created() {
    this.mode = decodeURIComponent(
      new URLSearchParams(window.location.search).get("mode"),
    );
    this.actionCode = decodeURIComponent(
      new URLSearchParams(window.location.search).get("oobCode"),
    );

    if (!this.mode || !this.actionCode) {
      this.$router.push({ name: "Login" });
    }
    if (this.mode === "verifyEmail") {
      this.verifyEmail();
    }
    if (this.mode === "verifyAndChangeEmail") {
      this.verifyEmailChange();
    }
    if (this.mode === "recoverEmail") {
      this.recoverEmail();
    }
  },
  methods: {
    logout() {
      this.authStore.logout();
    },
    async verifyEmailChange() {
      try {
        // Apply the action code to verify and change the email
        await applyActionCode(firebaseAuth, this.actionCode);

        // Refresh the auth token to get updated user info
        await this.authStore.refreshAuthIdToken();

        this.emailChangeVerified = true;
        setSuccessToast({
          message: this.$t("auth.email_change_success"),
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error verifying email change:", error);
        setErrorToast({
          message: this.$t("auth.email_change_verification_failed"),
        });
        this.emailChangeVerified = false;
        // Redirect to login after a delay
        setTimeout(() => {
          this.$router.push({ name: "Login" });
        }, 3000);
      }
    },
    async recoverEmail() {
      try {
        // First check the action code to get info about the email recovery
        const info = await checkActionCode(firebaseAuth, this.actionCode);
        console.log("Email recovery info:", info);

        // Apply the action code to restore the original email
        await applyActionCode(firebaseAuth, this.actionCode);

        // Refresh the auth token to get updated user info
        this.authStore.refreshAuthIdToken();

        this.emailRecovered = true;
        setSuccessToast({
          message: this.$t("auth.email_recover_success"),
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error recovering email:", error);
        setErrorToast({
          message: this.$t("auth.email_recover_failed"),
        });
        this.emailRecovered = false;
        // Redirect to login after a delay
        setTimeout(() => {
          this.$router.push({ name: "Login" });
        }, 3000);
      }
    },
    continueToApp() {
      this.$router.push({ name: "Group" });
    },
    async verifyEmail() {
      try {
        await applyActionCode(firebaseAuth, this.actionCode);
        setSuccessToast({
          message: this.$t("global.toast.email_verified_successfully"),
        });
        this.successfullVerified = true;
        await this.authStore.refreshAuthIdToken();
      } catch {
        setErrorToast({
          message: this.$t("global.toast.error_email_not_verified"),
        });
        this.$router.push({ name: "Login" });
      }
      try {
        this.$router.push({ name: "Group" });
      } catch {
        this.$router.push({ name: "Login" });
      }
    },
    async resetPassword() {
      try {
        this.resetting = true;
        let email = await verifyPasswordResetCode(
          firebaseAuth,
          this.actionCode,
        );
        try {
          await confirmPasswordReset(
            firebaseAuth,
            this.actionCode,
            this.password,
          );
          setSuccessToast({
            message: this.$t("global.toast.password_changed"),
          });
          try {
            await signInWithEmailAndPassword(
              firebaseAuth,
              email,
              this.password,
            );
            await this.userStore.refreshUserData();
            await this.groupsStore.refreshAllUserGroupsData();
            await this.$router.push({ name: "Group" });
          } catch {
            this.$router.push({ name: "Login" });
          }
        } catch {
          setErrorToast({
            message: this.$t("global.toast.password_not_changed"),
          });
          this.$router.push({ name: "Login" });
        }
      } catch {
        setErrorToast({
          message: this.$t("global.toast.password_link_not_valid"),
        });
        this.$router.push({ name: "Login" });
      } finally {
        this.resetting = false;
      }
    },
  },
};
</script>
